import axios from "axios";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: BASE,
  withCredentials: true,
});

// --- Environment-aware token store -------------------------------------------
// Security model:
//  - Access token lives in MEMORY only (never localStorage) so it is not
//    persistently stealable via XSS.
//  - Auth/refresh are primarily carried by the backend's httpOnly, Secure,
//    SameSite=None cookies. On a first-party domain (fightfund.com) that cookie
//    is the ONLY place a token lives in the browser.
//  - FALLBACK: the Emergent preview runs inside a 3rd-party iframe where browsers
//    block 3rd-party cookies. ONLY in that case do we persist a refresh token to
//    localStorage so login survives reloads. Detected at runtime via a cookie probe.
const REFRESH_KEY = "ff_refresh_token";
const MODE_KEY = "ff_auth_mode"; // "fallback" when cookies are blocked (preview iframe)

let memAccess = null; // in-memory access token
let memRefresh = null; // in-memory refresh token

export const tokenStore = {
  getAccess: () => memAccess,
  getRefresh: () => memRefresh || localStorage.getItem(REFRESH_KEY),
  setTokens: (access, refresh) => {
    if (access !== undefined) memAccess = access;
    if (refresh !== undefined && refresh !== null) memRefresh = refresh;
  },
  // Persist a refresh token to localStorage — only when cookie auth is unavailable.
  persistFallback: () => {
    if (memRefresh) localStorage.setItem(REFRESH_KEY, memRefresh);
    localStorage.setItem(MODE_KEY, "fallback");
  },
  clear: () => {
    memAccess = null;
    memRefresh = null;
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(MODE_KEY);
    localStorage.removeItem("ff_access_token"); // legacy key cleanup
  },
};

// Probe whether httpOnly cookie auth works (raw axios → NO bearer header attached).
// Returns true on first-party domains where the session cookie is honored.
export async function probeCookieAuth() {
  try {
    await axios.get(`${BASE}/auth/me`, { withCredentials: true });
    return true;
  } catch (e) {
    return false;
  }
}

// Raw refresh (bypasses the api-instance interceptors). Uses the cookie and, if a
// fallback refresh token exists, the Authorization header too.
export async function rawRefresh() {
  const headers = {};
  const r = tokenStore.getRefresh();
  if (r) headers.Authorization = `Bearer ${r}`;
  const { data } = await axios.post(`${BASE}/auth/refresh`, {}, { withCredentials: true, headers });
  if (data.access_token) memAccess = data.access_token;
  return data.access_token || null;
}

api.interceptors.request.use((config) => {
  if (memAccess) config.headers.Authorization = `Bearer ${memAccess}`;
  return config;
});

let refreshing = null;
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      try {
        if (!refreshing) refreshing = rawRefresh().finally(() => { refreshing = null; });
        const newAccess = await refreshing;
        if (newAccess) {
          original.headers.Authorization = `Bearer ${newAccess}`;
          return api(original);
        }
      } catch (e) {
        tokenStore.clear();
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function errMsg(e) {
  return formatApiError(e?.response?.data?.detail) || e?.message || "Request failed";
}

export const CATEGORIES = [
  "Animals", "Community", "Education", "Emergency", "Faith", "Family", "Legal",
  "Medical", "Memorial", "Nonprofit", "Small Business", "Sports", "Veterans",
];

export function money(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
}

// Opens a private document (e.g. nonprofit verification docs) via a short-lived signed token.
// Owner + admins only; the raw /api/files/.../documents/... URL is not publicly accessible.
export async function openSignedDoc(doc) {
  const w = window.open("", "_blank");
  try {
    const url = doc?.url || "";
    const path = url.split("/api/files/")[1];
    if (!path) throw new Error("Invalid document");
    const { data } = await api.get("/uploads/sign-document", { params: { path } });
    const signed = `${url}${url.includes("?") ? "&" : "?"}token=${encodeURIComponent(data.token)}`;
    if (w) w.location.href = signed;
    else window.open(signed, "_blank");
  } catch (e) {
    if (w) w.close();
    throw e;
  }
}
